import { useAtom } from "jotai";
import { useMemo, useCallback, useState } from "react";
import { periodFromDateAtom, periodToDateAtom } from "store/UIHrPageDashboard";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ICompanyInfosInfo } from "library/types";
import { Button, tokens, Tooltip } from "@fluentui/react-components";
import {
  ChevronLeftFilled,
  ChevronRightFilled,
} from "@fluentui/react-icons";

interface Props {
  companyInfo?: ICompanyInfosInfo;
}

const STEP_MONTHS = 6;

const generateMonths = (startDate: Date, endDate: Date): Date[] => {
  const months: Date[] = [];
  const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  while (current <= end) {
    months.push(new Date(current));
    current.setMonth(current.getMonth() + 1);
  }
  return months;
};

const getSafeLocale = (locale: string): string => {
  try {
    Intl.DateTimeFormat.supportedLocalesOf([locale]);
    return locale;
  } catch {
    return "en-US";
  }
};

const formatMonthShort = (date: Date, locale: string): string => {
  const safeLocale = getSafeLocale(locale);
  try {
    return date.toLocaleDateString(safeLocale, {
      month: "short",
      year: "numeric",
    });
  } catch {
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  }
};

const FilterPeriod = ({ companyInfo }: Props) => {
  const [periodFromDate, setPeriodFromDate] = useAtom(periodFromDateAtom);
  const [periodToDate, setPeriodToDate] = useAtom(periodToDateAtom);
  const { t, i18n } = useTranslation();

  const earliestDate = useMemo(() => {
    if (!companyInfo?.employeeEnrollments?.length) {
      return new Date(new Date().getFullYear(), 0, 1);
    }
    const dates = companyInfo.employeeEnrollments
      .map((en) => (en.dateOfEnrollment ? new Date(en.dateOfEnrollment) : null))
      .filter((d): d is Date => d !== null);

    if (dates.length === 0) {
      return new Date(new Date().getFullYear(), 0, 1);
    }
    return dates.reduce((min, d) => (d < min ? d : min), dates[0]!);
  }, [companyInfo?.employeeEnrollments]);

  const defaultEndMonth = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }, []);

  const [rangeEndMonth, setRangeEndMonth] = useState<Date>(defaultEndMonth);
  const [rangeStartOverride, setRangeStartOverride] = useState<Date | null>(
    null,
  );

  const effectiveStartMonth = useMemo(() => {
    if (rangeStartOverride && rangeStartOverride < earliestDate) {
      return rangeStartOverride;
    }
    return earliestDate;
  }, [rangeStartOverride, earliestDate]);

  const months = useMemo(
    () => generateMonths(effectiveStartMonth, rangeEndMonth),
    [effectiveStartMonth, rangeEndMonth],
  );

  const fromIndex = useMemo(() => {
    const from = new Date(periodFromDate);
    const idx = months.findIndex(
      (m) =>
        m.getFullYear() === from.getFullYear() &&
        m.getMonth() === from.getMonth(),
    );
    return idx >= 0 ? idx : 0;
  }, [periodFromDate, months]);

  const toIndex = useMemo(() => {
    const to = new Date(periodToDate);
    const idx = months.findIndex(
      (m) =>
        m.getFullYear() === to.getFullYear() && m.getMonth() === to.getMonth(),
    );
    return idx >= 0 ? idx : months.length - 1;
  }, [periodToDate, months]);

  const handleFromChange = useCallback(
    (value: number) => {
      const month = months[value];
      if (!month) return;
      const dateStr = `${month.getFullYear()}-${(month.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-01T00:00:00Z`;
      setPeriodFromDate(dateStr);
    },
    [months, setPeriodFromDate],
  );

  const handleToChange = useCallback(
    (value: number) => {
      const month = months[value];
      if (!month) return;
      setPeriodToDate(new Date(month));
    },
    [months, setPeriodToDate],
  );

  const canExtendLeft = useMemo(() => {
    const floor = new Date(
      earliestDate.getFullYear() - 5,
      earliestDate.getMonth(),
      1,
    );
    return effectiveStartMonth > floor;
  }, [effectiveStartMonth, earliestDate]);

  const canExtendRight = useMemo(() => {
    const now = new Date();
    const ceiling = new Date(now.getFullYear() + 5, now.getMonth(), 1);
    return rangeEndMonth < ceiling;
  }, [rangeEndMonth]);

  const handleExtendLeft = useCallback(() => {
    setRangeStartOverride((prev) => {
      const base = prev ?? earliestDate;
      return new Date(base.getFullYear(), base.getMonth() - STEP_MONTHS, 1);
    });
  }, [earliestDate]);

  const handleExtendRight = useCallback(() => {
    setRangeEndMonth((prev) => {
      return new Date(prev.getFullYear(), prev.getMonth() + STEP_MONTHS, 1);
    });
  }, []);

  const maxIndex = months.length - 1;
  const leftPercent = maxIndex > 0 ? (fromIndex / maxIndex) * 100 : 0;
  const rightPercent = maxIndex > 0 ? (toIndex / maxIndex) * 100 : 100;

  const yearTicks = useMemo(() => {
    const ticks: { label: string; percent: number }[] = [];
    months.forEach((m, i) => {
      if (m.getMonth() === 0) {
        ticks.push({
          label: m.getFullYear().toString(),
          percent: maxIndex > 0 ? (i / maxIndex) * 100 : 50,
        });
      }
    });
    return ticks;
  }, [months, maxIndex]);

  if (months.length < 2) {
    return (
      <Container>
        <HeaderRow>
          <PeriodLabel>{t("hbh.period.label")}:</PeriodLabel>
          <RangeBadge>
            {formatMonthShort(months[0] ?? new Date(), i18n.language)}
          </RangeBadge>
        </HeaderRow>
      </Container>
    );
  }

  return (
    <Container>
      <HeaderRow>
        <PeriodLabel>{t("hbh.period.label")}:</PeriodLabel>
        <RangeBadge>
          01. {formatMonthShort(months[fromIndex]!, i18n.language)} —{" "}
          01. {formatMonthShort(months[toIndex]!, i18n.language)}
        </RangeBadge>
      </HeaderRow>
      <SliderRow>
        <Tooltip
          content={t("hbh.period.extendEarlier")}
          relationship="label"
        >
          <StepperButton
            icon={<ChevronLeftFilled />}
            appearance="subtle"
            size="small"
            disabled={!canExtendLeft}
            onClick={handleExtendLeft}
            aria-label={t("hbh.period.extendEarlier")}
          />
        </Tooltip>
        <SliderWrapper>
          <SliderTrack>
            <TrackHighlight
              style={{
                left: `${leftPercent}%`,
                width: `${rightPercent - leftPercent}%`,
              }}
            />
          </SliderTrack>
          <StyledRange
            type="range"
            min={0}
            max={maxIndex}
            value={fromIndex}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (val < toIndex) handleFromChange(val);
            }}
          />
          <StyledRange
            type="range"
            min={0}
            max={maxIndex}
            value={toIndex}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (val > fromIndex) handleToChange(val);
            }}
          />
          <TickContainer>
            {yearTicks.map((tick) => (
              <TickItem key={tick.label} style={{ left: `${tick.percent}%` }}>
                <TickLine />
                <TickText>{tick.label}</TickText>
              </TickItem>
            ))}
          </TickContainer>
        </SliderWrapper>
        <Tooltip
          content={t("hbh.period.extendLater")}
          relationship="label"
        >
          <StepperButton
            icon={<ChevronRightFilled />}
            appearance="subtle"
            size="small"
            disabled={!canExtendRight}
            onClick={handleExtendRight}
            aria-label={t("hbh.period.extendLater")}
          />
        </Tooltip>
      </SliderRow>
    </Container>
  );
};

export default FilterPeriod;

/* ── Styled components ────────────────────────────────────────────── */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  width: 100%;
  gap: 8px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PeriodLabel = styled.label`
  font-weight: 600;
  white-space: nowrap;
`;

const RangeBadge = styled.span`
  font-size: 13px;
  color: ${tokens.colorBrandForeground1};
  font-weight: 600;
  background: ${tokens.colorBrandBackground2};
  padding: 2px 10px;
  border-radius: 12px;
`;

const SliderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
`;

const StepperButton = styled(Button)`
  flex-shrink: 0;
  min-width: 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  margin-top: 4px;
  border-radius: 50%;
  color: ${tokens.colorBrandForeground1};

  &:hover:not(:disabled) {
    background: ${tokens.colorBrandBackground2};
  }

  &:disabled {
    color: ${tokens.colorNeutralForegroundDisabled};
  }
`;

const SliderWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  height: 48px;
  padding-top: 8px;
`;

const SliderTrack = styled.div`
  position: absolute;
  top: 16px;
  left: 0;
  right: 0;
  height: 6px;
  background: ${tokens.colorNeutralBackground5};
  border-radius: 3px;
`;

const TrackHighlight = styled.div`
  position: absolute;
  height: 100%;
  background: ${tokens.colorBrandBackground};
  border-radius: 3px;
  transition:
    left 0.1s ease,
    width 0.1s ease;
`;

const THUMB_SIZE = 20;

const StyledRange = styled.input`
  position: absolute;
  top: 8px;
  left: 0;
  width: 100%;
  height: ${THUMB_SIZE}px;
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
  pointer-events: none;
  margin: 0;
  padding: 0;
  z-index: 2;

  &::-webkit-slider-runnable-track {
    height: 6px;
    background: transparent;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    pointer-events: all;
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    border-radius: 50%;
    background: ${tokens.colorBrandBackground};
    border: 2px solid ${tokens.colorNeutralBackground1};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    margin-top: -7px;
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease;
  }

  &:hover::-webkit-slider-thumb {
    transform: scale(1.2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &::-moz-range-track {
    height: 6px;
    background: transparent;
    border: none;
  }

  &::-moz-range-thumb {
    pointer-events: all;
    width: ${THUMB_SIZE - 4}px;
    height: ${THUMB_SIZE - 4}px;
    border-radius: 50%;
    background: ${tokens.colorBrandBackground};
    border: 2px solid ${tokens.colorNeutralBackground1};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    cursor: pointer;
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease;
  }

  &:hover::-moz-range-thumb {
    transform: scale(1.2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible::-webkit-slider-thumb {
    box-shadow: 0 0 0 2px ${tokens.colorBrandStroke1};
  }
`;

const TickContainer = styled.div`
  position: absolute;
  top: 28px;
  left: 0;
  right: 0;
  height: 20px;
`;

const TickItem = styled.div`
  position: absolute;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TickLine = styled.div`
  width: 1px;
  height: 6px;
  background: ${tokens.colorNeutralStroke2};
`;

const TickText = styled.span`
  font-size: 10px;
  color: ${tokens.colorNeutralForeground3};
  white-space: nowrap;
  margin-top: 1px;
`;
